import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import {
  Megaphone,
  Plug,
  RefreshCw,
  Target,
  CheckCircle2,
  AlertTriangle,
  Building2,
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function GoogleAds() {
  const qc = useQueryClient();
  const [search, setSearch] = useSearchParams();

  const { data: status } = useQuery({
    queryKey: ['google-ads-status'],
    queryFn: () => apiGet('/google-ads/status'),
    refetchInterval: 10_000,
  });
  const { data: accounts } = useQuery({
    queryKey: ['google-ads-accounts'],
    queryFn: () => apiGet('/google-ads/accounts'),
    enabled: !!status?.connected,
  });
  const { data: convActions } = useQuery({
    queryKey: ['google-ads-conversion-actions', status?.connection?.customerId],
    queryFn: () => apiGet('/google-ads/conversion-actions'),
    enabled: !!status?.connected && !!status?.connection?.customerId,
  });

  useEffect(() => {
    const g = search.get('google');
    if (g === 'connected') {
      toast.success('Google Ads connected');
      search.delete('google');
      setSearch(search);
    } else if (g === 'error') {
      toast.error(`Google Ads error: ${search.get('msg') || 'unknown'}`);
      search.delete('google');
      search.delete('msg');
      setSearch(search);
    }
  }, [search, setSearch]);

  async function connect() {
    try {
      const r = await apiGet('/google-ads/oauth-url');
      window.location.href = r.url;
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function disconnect() {
    try {
      await apiPost('/google-ads/disconnect', {});
      toast.success('Disconnected');
      qc.invalidateQueries({ queryKey: ['google-ads-status'] });
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function syncMetrics() {
    try {
      const r = await apiPost('/google-ads/sync/metrics', { days: 7 });
      toast.success(`Synced ${r.count} campaign-day rows`);
      qc.invalidateQueries({ queryKey: ['google-ads-status'] });
    } catch (e) {
      toast.error(e.message);
    }
  }

  const conn = status?.connection;
  const isConnected = !!status?.connected;

  return (
    <div className="space-y-4">
      <TopBar
        title="Google Ads"
        subtitle="Native OAuth · Spend ingest · Offline conversion uploads via gclid"
      />

      {!status?.available && (
        <div className="panel p-4 border border-yellow-500/30 bg-yellow-500/5">
          <div className="flex items-start gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5" />
            <div>
              <div className="font-medium text-yellow-200">Stub mode</div>
              <div className="text-xs text-white/60 mt-1">
                Set <code className="text-civix-cyan">GOOGLE_OAUTH_CLIENT_ID</code>,{' '}
                <code className="text-civix-cyan">GOOGLE_OAUTH_CLIENT_SECRET</code>, and{' '}
                <code className="text-civix-cyan">GOOGLE_ADS_DEVELOPER_TOKEN</code> on Render to
                enable real Google Ads integration. The OAuth URL, accounts, metrics, and offline
                conversions will return stub data until then. Developer tokens require a 1–5 day
                approval at{' '}
                <a
                  href="https://ads.google.com/aw/apicenter"
                  target="_blank"
                  rel="noreferrer"
                  className="text-civix-cyan underline"
                >
                  ads.google.com/aw/apicenter
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="panel p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm text-white/70">
          <Megaphone className="w-4 h-4" /> Connection
        </div>

        {!isConnected ? (
          <div className="grid md:grid-cols-1 gap-3">
            <button
              onClick={connect}
              className="text-left p-4 rounded-md border border-civix-purple/50 bg-civix-purple/5 hover:border-civix-purple"
            >
              <div className="font-medium flex items-center gap-2">
                <Plug className="w-4 h-4" /> Connect Google Ads
              </div>
              <div className="text-xs text-white/60 mt-2">
                CIVIX pulls daily campaign spend & metrics, and uploads offline conversions back to
                Google whenever a CIVIX lead is marked sold (via the captured <code>gclid</code>).
                Admin-only — only the owner of the ad account should connect.
              </div>
              <div className="text-xs text-civix-cyan mt-3">Sign in with Google →</div>
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs px-2 py-0.5 rounded-sm bg-green-500/20 text-green-300 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Connected
              </span>
              {conn?.googleEmail && (
                <span className="text-xs text-white/50">{conn.googleEmail}</span>
              )}
              {conn?.lastError && (
                <span className="text-xs px-2 py-0.5 rounded-sm bg-red-500/20 text-red-300">
                  {conn.lastError}
                </span>
              )}
              <button
                onClick={disconnect}
                className="ml-auto text-xs px-2 py-1 rounded-sm border border-white/10 hover:border-red-400/40 hover:text-red-300"
              >
                Disconnect
              </button>
            </div>

            <CustomerPanel
              accounts={accounts?.items || []}
              currentCustomerId={conn?.customerId}
              currentManager={conn?.managerCustomerId}
              currentName={conn?.customerName}
              currency={conn?.currencyCode}
              timeZone={conn?.timeZone}
              onChanged={() => qc.invalidateQueries({ queryKey: ['google-ads-status'] })}
            />

            <ConversionActionPanel
              items={convActions?.items || []}
              currentResource={conn?.soldConversionActionResource}
              currentName={conn?.soldConversionActionName}
              disabled={!conn?.customerId}
              onChanged={() => qc.invalidateQueries({ queryKey: ['google-ads-status'] })}
            />

            <div className="border-t border-white/5 pt-4 flex items-end gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm text-white/70 mb-2">
                  <RefreshCw className="w-4 h-4" /> Manual Sync
                </div>
                <div className="text-xs text-white/40">
                  Pulls last 7 days of campaign-level cost / clicks / impressions / conversions into
                  CIVIX for the Lead ROI engine. Cron does this hourly automatically.
                </div>
              </div>
              <button
                onClick={syncMetrics}
                disabled={!conn?.customerId}
                className="px-3 py-1.5 rounded-md bg-linear-to-r from-civix-purple to-civix-cyan text-sm font-medium disabled:opacity-50"
              >
                Sync now
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <Stat label="Customer" value={conn?.customerId ? formatCid(conn.customerId) : '—'} />
              <Stat
                label="Last Metrics Sync"
                value={conn?.lastMetricsSyncAt ? new Date(conn.lastMetricsSyncAt).toLocaleString() : '—'}
              />
              <Stat
                label="Conversions Uploaded"
                value={conn?.totalConversionsUploaded || 0}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CustomerPanel({ accounts, currentCustomerId, currentManager, currentName, currency, timeZone, onChanged }) {
  const [customerId, setCustomerId] = useState(currentCustomerId || '');
  const [managerId, setManagerId] = useState(currentManager || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setCustomerId(currentCustomerId || ''); }, [currentCustomerId]);
  useEffect(() => { setManagerId(currentManager || ''); }, [currentManager]);

  async function save() {
    setSaving(true);
    try {
      await apiPost('/google-ads/select-customer', {
        customerId,
        managerCustomerId: managerId,
      });
      toast.success('Saved');
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid md:grid-cols-2 gap-3 border-t border-white/5 pt-4">
      <div className="md:col-span-2 flex items-center gap-2 text-sm text-white/70">
        <Building2 className="w-4 h-4" /> Account
        {currentName && (
          <span className="text-xs text-white/40">
            · {currentName} {currency ? `(${currency})` : ''} {timeZone ? `· ${timeZone}` : ''}
          </span>
        )}
      </div>
      <div>
        <label className="block text-xs text-white/60 mb-1">Customer (Ads account)</label>
        <select
          className="input w-full"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
        >
          <option value="">Select…</option>
          {accounts.map((a) => (
            <option key={a.customerId || a.id} value={a.customerId || a.id}>
              {formatCid(a.customerId || a.id)} {a.descriptiveName ? `· ${a.descriptiveName}` : ''}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-white/60 mb-1">
          Manager (MCC) — optional
        </label>
        <input
          className="input w-full"
          placeholder="login_customer_id (digits only)"
          value={managerId}
          onChange={(e) => setManagerId(e.target.value)}
        />
        <div className="text-xs text-white/40 mt-1">
          Required only if you access the customer through a manager account.
        </div>
      </div>
      <div className="md:col-span-2 text-right">
        <button
          onClick={save}
          disabled={!customerId || saving}
          className="px-3 py-1.5 rounded-md bg-linear-to-r from-civix-purple to-civix-cyan text-sm font-medium disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Account'}
        </button>
      </div>
    </div>
  );
}

function ConversionActionPanel({ items, currentResource, currentName, disabled, onChanged }) {
  const [resourceName, setResourceName] = useState(currentResource || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setResourceName(currentResource || ''); }, [currentResource]);

  async function save() {
    setSaving(true);
    try {
      const item = items.find((i) => i.resourceName === resourceName);
      await apiPost('/google-ads/select-conversion-action', {
        resourceName,
        name: item?.name || '',
      });
      toast.success('Saved');
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border-t border-white/5 pt-4 space-y-2">
      <div className="flex items-center gap-2 text-sm text-white/70">
        <Target className="w-4 h-4" /> Sold Conversion Action
        {currentName && <span className="text-xs text-white/40">· {currentName}</span>}
      </div>
      <div className="text-xs text-white/40">
        When a CIVIX lead disposition flips to sold, we upload the conversion to this action using
        the lead's <code>gclid</code>. Pick the conversion you defined in Google Ads (typically a
        "Purchase" or "Sold Policy" import action).
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <select
            className="input w-full"
            value={resourceName}
            onChange={(e) => setResourceName(e.target.value)}
            disabled={disabled}
          >
            <option value="">{disabled ? 'Pick a customer first…' : 'Select…'}</option>
            {items.map((i) => (
              <option key={i.resourceName} value={i.resourceName}>
                {i.name} {i.category ? `· ${i.category}` : ''} {i.status ? `· ${i.status}` : ''}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={save}
          disabled={!resourceName || saving}
          className="px-3 py-1.5 rounded-md bg-linear-to-r from-civix-purple to-civix-cyan text-sm font-medium disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/5 rounded-md p-3">
      <div className="text-xs text-white/50 uppercase tracking-wider">{label}</div>
      <div className="text-lg font-medium mt-1 truncate">{value}</div>
    </div>
  );
}

function formatCid(cid) {
  if (!cid) return '';
  const s = String(cid).replace(/\D/g, '');
  if (s.length !== 10) return cid;
  return `${s.slice(0, 3)}-${s.slice(3, 6)}-${s.slice(6)}`;
}
